"use client";

/**
 * Accent colour theme — independent of light/dark mode (that's next-themes'
 * job via `attribute="class"`). Deliberately NOT a second next-themes
 * ThemeProvider: next-themes' useTheme() reads from a single shared React
 * context, so nesting a second instance would shadow the light/dark
 * ThemeProvider for every descendant and break the existing ThemeToggle.
 * This is a standalone Context + localStorage + inline no-flash script,
 * mirroring next-themes' own approach but on its own data-accent attribute.
 */

import { createContext, useContext, useEffect, useState } from "react";

export const ACCENTS = ["teal", "black", "purple", "blue", "pink", "violet", "indigo", "orange", "bronze", "mint"] as const;
export type Accent = (typeof ACCENTS)[number];

const STORAGE_KEY = "obel-accent";
const DEFAULT_ACCENT: Accent = "teal";

/**
 * Mirrors the --c-brand values declared per accent/mode in globals.css.
 * Exported so components that need a guaranteed-correct color for THIS
 * exact render (e.g. the collapsed sidebar's own background-color) can
 * read it directly in JS instead of depending on CSS var() resolution —
 * sidesteps any cascade-layer/specificity ordering against the org-brand
 * override that a plain var(--c-brand) lookup is otherwise subject to.
 * fg = a readable text/icon color for content painted ON TOP of that bg.
 */
export const ACCENT_HEX: Record<Accent, { light: string; dark: string; fgLight: string; fgDark: string }> = {
  teal:   { light: "#0d9488", dark: "#2dd4bf", fgLight: "#ffffff", fgDark: "#0d1117" },
  black:  { light: "#111827", dark: "#e5e7eb", fgLight: "#ffffff", fgDark: "#111827" },
  purple: { light: "#7c3aed", dark: "#a78bfa", fgLight: "#ffffff", fgDark: "#1e1033" },
  blue:   { light: "#2563eb", dark: "#60a5fa", fgLight: "#ffffff", fgDark: "#0b1f3f" },
  pink:   { light: "#db2777", dark: "#f472b6", fgLight: "#ffffff", fgDark: "#3f0b23" },
  violet: { light: "#8b5cf6", dark: "#c4b5fd", fgLight: "#ffffff", fgDark: "#241548" },
  indigo: { light: "#4f46e5", dark: "#818cf8", fgLight: "#ffffff", fgDark: "#151046" },
  orange: { light: "#ea580c", dark: "#fb923c", fgLight: "#ffffff", fgDark: "#3f1c05" },
  bronze: { light: "#9a5b13", dark: "#c9862f", fgLight: "#ffffff", fgDark: "#2e1a06" },
  mint:   { light: "#059669", dark: "#34d399", fgLight: "#ffffff", fgDark: "#052e21" },
};

interface AccentContextValue {
  accent: Accent;
  setAccent: (a: Accent) => void;
}

const AccentContext = createContext<AccentContextValue | null>(null);

export function useAccent(): AccentContextValue {
  const ctx = useContext(AccentContext);
  if (!ctx) throw new Error("useAccent must be used within AccentProvider");
  return ctx;
}

const NO_FLASH_SCRIPT = `
(function () {
  try {
    var a = localStorage.getItem("${STORAGE_KEY}");
    if (a && a !== "${DEFAULT_ACCENT}") document.documentElement.setAttribute("data-accent", a);
  } catch (e) {}
})();
`;

export default function AccentProvider({ children }: { children: React.ReactNode }) {
  const [accent, setAccentState] = useState<Accent>(DEFAULT_ACCENT);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as Accent | null;
    if (stored && ACCENTS.includes(stored)) setAccentState(stored);
  }, []);

  const setAccent = (a: Accent) => {
    setAccentState(a);
    localStorage.setItem(STORAGE_KEY, a);
    if (a === DEFAULT_ACCENT) {
      document.documentElement.removeAttribute("data-accent");
    } else {
      document.documentElement.setAttribute("data-accent", a);
    }
  };

  return (
    <AccentContext.Provider value={{ accent, setAccent }}>
      <script dangerouslySetInnerHTML={{ __html: NO_FLASH_SCRIPT }} />
      {children}
    </AccentContext.Provider>
  );
}
